import React, { useState, useMemo, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ContactLogo from '../components/shared/ContactLogo';
import { getDocsCategories, getDocsArticles, getDocsFaqs, getDocsPopularSlugs } from '../data/docsContent';
import { LEGAL_ES } from '../constants/legalContent';

const RuixenGradientFooter = lazy(() =>
  import('@/components/ui/ruixen-gradient-footer').then((m) => ({ default: m.RuixenGradientFooter }))
);

const LANG_STORAGE_KEY = 'contact_docs_lang';
const THEME_STORAGE_KEY = 'contact_docs_theme';

/* UI copy for everything in the chrome around the content itself (search,
   nav, footer, modals). The actual documentation text lives in
   data/docsContent.js, keyed the same way (es/en/pt). */
const UI = {
  es: {
    metaTitleSuffix: 'Docs Contact',
    metaTitleHome: 'Documentación · Contact',
    openMenu: 'Abrir menú',
    search: 'Buscar',
    docsLink: 'Docs',
    searchPlaceholder: 'Buscar en la documentación…',
    backHome: 'Volver al inicio',
    login: 'Acceder',
    contactSupport: 'Contactar soporte',
    faqTitle: 'Preguntas frecuentes',
    heroTitle: '¿En qué te podemos ayudar?',
    heroSub: 'Todo lo que necesitas saber sobre tu tarjeta Contact, tu perfil digital y el panel. Explora el menú de la izquierda o busca directamente.',
    mostSearched: 'Más buscado',
    prev: 'Anterior',
    next: 'Siguiente',
    mobileSearchHint: 'Escribe para buscar en toda la documentación.',
    noResultsFor: (q) => `Sin resultados para "${q}".`,
    closeSearch: 'Cerrar búsqueda',
    closeModal: 'Cerrar',
    onThisPage: 'En esta página',
    toggleTheme: 'Cambiar tema',
    changeLanguage: 'Cambiar idioma',
    footer: {
      product: 'Producto', features: 'Características', plans: 'Planes', schedule: 'Agendar reunión', docs: 'Documentación',
      company: 'Empresa', about: 'Nosotros',
      legal: 'Legal', terms: 'Términos y Condiciones', privacy: 'Política de Privacidad', dataProcessing: 'Tratamiento de Datos', hours: 'Lun–Vie 8:00–20:00 hrs',
      security: 'Seguridad', securityText: 'Tus datos viajan cifrados con TLS y nunca los compartimos con terceros. Tú decides qué información mostrar y cuándo actualizarla.',
      copyright: '© 2026 CONTACT Network SPA · Chile', tagline: 'Conecta. Comparte. Crece.',
    },
  },
  en: {
    metaTitleSuffix: 'Contact Docs',
    metaTitleHome: 'Documentation · Contact',
    openMenu: 'Open menu',
    search: 'Search',
    docsLink: 'Docs',
    searchPlaceholder: 'Search the documentation…',
    backHome: 'Back to home',
    login: 'Log in',
    contactSupport: 'Contact support',
    faqTitle: 'Frequently asked questions',
    heroTitle: 'How can we help you?',
    heroSub: 'Everything you need to know about your Contact card, your digital profile and the dashboard. Browse the menu on the left or search directly.',
    mostSearched: 'Most searched',
    prev: 'Previous',
    next: 'Next',
    mobileSearchHint: 'Type to search the whole documentation.',
    noResultsFor: (q) => `No results for "${q}".`,
    closeSearch: 'Close search',
    closeModal: 'Close',
    onThisPage: 'On this page',
    toggleTheme: 'Toggle theme',
    changeLanguage: 'Change language',
    footer: {
      product: 'Product', features: 'Features', plans: 'Pricing', schedule: 'Book a meeting', docs: 'Documentation',
      company: 'Company', about: 'About us',
      legal: 'Legal', terms: 'Terms and Conditions', privacy: 'Privacy Policy', dataProcessing: 'Data Processing', hours: 'Mon–Fri 8:00 AM–8:00 PM',
      security: 'Security', securityText: 'Your data travels encrypted with TLS and we never share it with third parties. You decide what information to show and when to update it.',
      copyright: '© 2026 CONTACT Network SPA · Chile', tagline: 'Connect. Share. Grow.',
    },
  },
  pt: {
    metaTitleSuffix: 'Docs Contact',
    metaTitleHome: 'Documentação · Contact',
    openMenu: 'Abrir menu',
    search: 'Buscar',
    docsLink: 'Docs',
    searchPlaceholder: 'Buscar na documentação…',
    backHome: 'Voltar ao início',
    login: 'Entrar',
    contactSupport: 'Contatar suporte',
    faqTitle: 'Perguntas frequentes',
    heroTitle: 'Como podemos ajudar você?',
    heroSub: 'Tudo o que você precisa saber sobre seu cartão Contact, seu perfil digital e o painel. Explore o menu à esquerda ou busque diretamente.',
    mostSearched: 'Mais buscado',
    prev: 'Anterior',
    next: 'Próximo',
    mobileSearchHint: 'Digite para buscar em toda a documentação.',
    noResultsFor: (q) => `Sem resultados para "${q}".`,
    closeSearch: 'Fechar busca',
    closeModal: 'Fechar',
    onThisPage: 'Nesta página',
    toggleTheme: 'Mudar tema',
    changeLanguage: 'Mudar idioma',
    footer: {
      product: 'Produto', features: 'Funcionalidades', plans: 'Planos', schedule: 'Agendar reunião', docs: 'Documentação',
      company: 'Empresa', about: 'Sobre nós',
      legal: 'Legal', terms: 'Termos e Condições', privacy: 'Política de Privacidade', dataProcessing: 'Tratamento de Dados', hours: 'Seg–Sex 8h–20h',
      security: 'Segurança', securityText: 'Seus dados trafegam criptografados com TLS e nunca são compartilhados com terceiros. Você decide quais informações mostrar e quando atualizá-las.',
      copyright: '© 2026 CONTACT Network SPA · Chile', tagline: 'Conecte. Compartilhe. Cresça.',
    },
  },
};

const LANG_OPTIONS = [
  { key: 'es', label: 'ES' },
  { key: 'en', label: 'EN' },
  { key: 'pt', label: 'PT' },
];

/* ─── ICONS ──────────────────────────────────────────────── */
const IconArrowLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 5l-7 7 7 7"/>
  </svg>
);
const IconArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);
const IconSearch = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>
  </svg>
);
const IconChevronRight = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5l7 7-7 7"/>
  </svg>
);
const IconChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6"/>
  </svg>
);
const IconMenu = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M3 12h18M3 18h18"/>
  </svg>
);
const IconX = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18M6 6l12 12"/>
  </svg>
);
const IconInfo = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M12 16v-5M12 8h.01"/>
  </svg>
);
const IconAlert = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/>
  </svg>
);
const IconStar = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2 9.5 8.5 2 9.3l5.7 4.9L6 21l6-3.9 6 3.9-1.7-6.8L22 9.3l-7.5-.8z"/>
  </svg>
);
const IconLifeBuoy = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><path d="m4.9 4.9 4.2 4.2M14.9 14.9l4.2 4.2M14.9 9.1l4.2-4.2M4.9 19.1l4.2-4.2"/>
  </svg>
);
const IconSun = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
  </svg>
);
const IconMoon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
  </svg>
);
const IconGlobe = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/>
  </svg>
);

/* Category icons, one simple glyph per category, same stroke style as
   the rest of the icon set above so the sidebar/cards don't feel like a
   different icon language was bolted on. */
const CATEGORY_ICONS = {
  play: (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M5 3l16 9-16 9V3z"/></svg>,
  id: (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="2" y="5" width="20" height="14" rx="2"/><circle cx="8.5" cy="12" r="2"/><path d="M14 10h6M14 14h4"/></svg>,
  users: (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  chart: (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M3 3v18h18"/><path d="M7 15l4-4 3 3 5-6"/></svg>,
  settings: (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  code: (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M16 18l6-6-6-6M8 6l-6 6 6 6"/></svg>,
  lightbulb: (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M9 18h6M10 22h4M15.09 14c.18-.98.65-1.74 1.41-2.5A6 6 0 1 0 7.5 11.5c.76.76 1.23 1.52 1.41 2.5"/></svg>,
  wrench: (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M14.7 6.3a4 4 0 0 0-5.6 5.6L2 19l3 3 7.1-7.1a4 4 0 0 0 5.6-5.6l-3 3-3-3 3-3z"/></svg>,
};

const FAQ_ANCHOR = 'preguntas-frecuentes';
const SUPPORT_LINK = { category: 'cuenta', slug: 'soporte' };

const Block = ({ block }) => {
  if (block.t === 'p') return <p className="dp-p">{block.c}</p>;
  if (block.t === 'h') return <h4 className="dp-h4">{block.c}</h4>;
  if (block.t === 'list') return (
    <ul className="dp-list">{block.items.map((it, i) => <li key={i}>{it}</li>)}</ul>
  );
  if (block.t === 'steps') return (
    <ol className="dp-steps">{block.items.map((it, i) => <li key={i}>{it}</li>)}</ol>
  );
  if (block.t === 'table') return (
    <div className="dp-table-wrap">
      <table className="dp-table">
        <thead><tr>{block.headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
        <tbody>{block.rows.map((row, ri) => (
          <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>
        ))}</tbody>
      </table>
    </div>
  );
  if (block.t === 'callout') return (
    <div className={`dp-callout dp-callout--${block.variant}`}>
      <span className="dp-callout-ico">{block.variant === 'warn' ? <IconAlert/> : <IconInfo/>}</span>
      <span>{block.c}</span>
    </div>
  );
  return null;
};

const FaqAccordion = ({ faqs }) => {
  const [open, setOpen] = useState(() => new Set([0]));
  const toggle = (i) => setOpen(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });
  return (
    <div className="dp-faq-list">
      {faqs.map((f, i) => {
        const isOpen = open.has(i);
        return (
          <div key={i} className={`dp-faq-item ${isOpen ? 'dp-faq-item--open' : ''}`}>
            <button className="dp-faq-q" onClick={() => toggle(i)}>
              {f.q}
              <span className="dp-faq-chevron"><IconChevronDown/></span>
            </button>
            {isOpen && <div className="dp-faq-a">{f.a}</div>}
          </div>
        );
      })}
    </div>
  );
};

const DocsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { category: categoryParam } = useParams();
  const [query, setQuery] = useState('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [activeSlug, setActiveSlug] = useState(null);
  const [showSecurity, setShowSecurity] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showDataProcessingModal, setShowDataProcessingModal] = useState(false);
  const [lang, setLang] = useState(() => {
    try {
      const stored = localStorage.getItem(LANG_STORAGE_KEY);
      if (stored === 'en' || stored === 'pt' || stored === 'es') return stored;
    } catch {}
    return 'es';
  });
  const [theme, setTheme] = useState(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === 'dark' || stored === 'light') return stored;
    } catch {}
    return 'dark';
  });
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const searchRef = useRef(null);
  const searchBoxRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const langMenuRef = useRef(null);
  const sectionRefs = useRef({});
  const pendingHash = useRef(null);

  const t = UI[lang] || UI.es;

  useEffect(() => {
    try { localStorage.setItem(LANG_STORAGE_KEY, lang); } catch {}
  }, [lang]);

  useEffect(() => {
    try { localStorage.setItem(THEME_STORAGE_KEY, theme); } catch {}
  }, [theme]);

  const DOCS_CATEGORIES = useMemo(() => getDocsCategories(lang), [lang]);
  const DOCS_ARTICLES = useMemo(() => getDocsArticles(lang), [lang]);
  const DOCS_FAQS = useMemo(() => getDocsFaqs(lang), [lang]);
  const ORDERED_ARTICLES = useMemo(
    () => DOCS_CATEGORIES.flatMap(cat => DOCS_ARTICLES.filter(a => a.category === cat.key)),
    [DOCS_CATEGORIES, DOCS_ARTICLES]
  );

  const category = categoryParam ? DOCS_CATEGORIES.find(c => c.key === categoryParam) : null;
  const sections = category ? DOCS_ARTICLES.filter(a => a.category === category.key) : [];
  const faqs = category ? (DOCS_FAQS[category.key] || []) : [];

  const catIdx = category ? DOCS_CATEGORIES.findIndex(c => c.key === category.key) : -1;
  const prevCategory = catIdx > 0 ? DOCS_CATEGORIES[catIdx - 1] : null;
  const nextCategory = catIdx >= 0 && catIdx < DOCS_CATEGORIES.length - 1 ? DOCS_CATEGORIES[catIdx + 1] : null;

  useEffect(() => {
    document.title = category ? `${category.title} · ${t.metaTitleSuffix}` : t.metaTitleHome;
  }, [category, t]);

  // Scroll to the right place whenever the category changes: to a specific
  // section if the URL carries a hash (e.g. coming from search or the home
  // page), otherwise to the top of the new page.
  useEffect(() => {
    setMobileNavOpen(false);
    setMobileSearchOpen(false);
    const hash = location.hash ? location.hash.slice(1) : null;
    if (hash) {
      pendingHash.current = hash;
      requestAnimationFrame(() => {
        const el = sectionRefs.current[hash];
        if (el) { el.scrollIntoView({ behavior: 'auto', block: 'start' }); setActiveSlug(hash); }
        pendingHash.current = null;
      });
    } else {
      window.scrollTo(0, 0);
      setActiveSlug(sections[0]?.slug || null);
    }
  }, [categoryParam, location.hash]); // eslint-disable-line

  // Cmd/Ctrl+K focuses search, Escape clears and blurs it: same conventions
  // Linear/Vercel-style docs use.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'Escape') {
        if (mobileSearchOpen) { setMobileSearchOpen(false); setQuery(''); }
        else if (document.activeElement === searchRef.current) { setQuery(''); searchRef.current?.blur(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileSearchOpen]);

  // Autofocus the moment the mobile full-screen search overlay opens.
  useEffect(() => {
    if (mobileSearchOpen) mobileSearchRef.current?.focus();
  }, [mobileSearchOpen]);

  // Close the search results dropdown on an outside click, so it never
  // lingers open over content once the reader has moved on.
  useEffect(() => {
    const onClick = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setQuery('');
      }
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Scroll-spy: highlight whichever section is currently under the top of
  // the viewport as the reader scrolls, same pattern Linear's docs use for
  // their right-rail "on this page" list. Multiple short sections (like the
  // FAQ entries) can be simultaneously "intersecting" at once; tracking all
  // currently-visible slugs and always picking the topmost one in document
  // order keeps the highlight deterministic instead of flickering between
  // whichever entry the browser happened to report last.
  useEffect(() => {
    if (!category) return;
    const order = [...sections.map(a => a.slug), ...(faqs.length ? [FAQ_ANCHOR] : [])];
    const visible = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const slug = entry.target.dataset.slug;
        if (entry.isIntersecting) visible.add(slug); else visible.delete(slug);
      });
      if (pendingHash.current || visible.size === 0) return;
      const topmost = order.find(slug => visible.has(slug));
      if (topmost) setActiveSlug(topmost);
    }, { rootMargin: '-96px 0px -70% 0px', threshold: 0 });
    Object.values(sectionRefs.current).forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [category]); // eslint-disable-line

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return ORDERED_ARTICLES
      .map(a => {
        const inTitle = a.title.toLowerCase().includes(q);
        const inExcerpt = a.excerpt.toLowerCase().includes(q);
        const inBody = a.blocks.some(b => (b.c || '').toLowerCase().includes(q) || (b.items || []).some(it => it.toLowerCase().includes(q)));
        const score = inTitle ? 3 : inExcerpt ? 2 : inBody ? 1 : 0;
        return { a, score };
      })
      .filter(r => r.score > 0)
      .sort((x, y) => y.score - x.score)
      .slice(0, 8)
      .map(r => r.a);
  }, [query, ORDERED_ARTICLES]);

  const popularArticles = useMemo(() => DOCS_ARTICLES.filter(a => a.popular), [DOCS_ARTICLES]);

  // The one piece of actual navigation logic: jump to a section. If it's
  // already on the page we're looking at, just scroll: no route change,
  // no reload, exactly the "don't make me leave to find it" behaviour that
  // was asked for. Only a different category triggers a real navigation.
  const jumpTo = useCallback((targetCategory, slug) => {
    setQuery('');
    setMobileNavOpen(false);
    setMobileSearchOpen(false);
    if (targetCategory === categoryParam) {
      const el = sectionRefs.current[slug];
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); setActiveSlug(slug); }
    } else {
      navigate(`/docs/${targetCategory}#${slug}`);
    }
  }, [categoryParam, navigate]);

  const goToCategory = (key) => {
    setQuery('');
    setMobileNavOpen(false);
    setMobileSearchOpen(false);
    if (key === categoryParam) {
      // Already here: a second click on the current category just means
      // "take me back to the top", not a no-op route change.
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setActiveSlug(sections[0]?.slug || null);
      return;
    }
    navigate(`/docs/${key}`);
  };

  const toggleTheme = () => setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));

  // Always-expanded sidebar: every category shows every one of its
  // sections at once (Vercel/Linear pattern), so anywhere in the docs is
  // one click away without first having to "enter" a category.
  const Sidebar = (
    <>
      <nav className="dp-sidebar-nav">
        {DOCS_CATEGORIES.map(cat => {
          const Icon = CATEGORY_ICONS[cat.icon];
          const arts = DOCS_ARTICLES.filter(a => a.category === cat.key);
          const isActiveCat = category?.key === cat.key;
          return (
            <div key={cat.key} className="dp-sidebar-group">
              <button
                className={`dp-sidebar-cat ${isActiveCat ? 'dp-sidebar-cat--active' : ''}`}
                onClick={() => goToCategory(cat.key)}
              >
                <span className="dp-sidebar-cat-ico"><Icon/></span>
                {cat.title}
              </button>
              <div className="dp-sidebar-arts">
                {arts.map(a => (
                  <button
                    key={a.slug}
                    className={`dp-sidebar-art ${isActiveCat && activeSlug === a.slug ? 'dp-sidebar-art--active' : ''}`}
                    onClick={() => jumpTo(cat.key, a.slug)}
                  >
                    {a.title}
                  </button>
                ))}
                {(DOCS_FAQS[cat.key] || []).length > 0 && (
                  <button
                    className={`dp-sidebar-art ${isActiveCat && activeSlug === FAQ_ANCHOR ? 'dp-sidebar-art--active' : ''}`}
                    onClick={() => jumpTo(cat.key, FAQ_ANCHOR)}
                  >
                    {t.faqTitle}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="dp-sidebar-footer">
        <button className="dp-sidebar-footer-link" onClick={() => jumpTo(SUPPORT_LINK.category, SUPPORT_LINK.slug)}>
          <IconLifeBuoy/> {t.contactSupport}
        </button>
      </div>
    </>
  );

  return (
    <div className="dp-root" data-theme={theme}>
      <header className="dp-topbar">
        <div className="dp-topbar-inner">
          <div className="dp-topbar-left">
            <button className="dp-mobile-menu-btn" onClick={() => setMobileNavOpen(v => !v)} aria-label={t.openMenu}>
              {mobileNavOpen ? <IconX/> : <IconMenu/>}
            </button>
            <ContactLogo clickable/>
            <span className="dp-topbar-divider"/>
            <button className="dp-topbar-docslink" onClick={() => navigate('/docs')}>{t.docsLink}</button>
          </div>
          <button className="dp-mobile-search-btn" onClick={() => setMobileSearchOpen(true)} aria-label={t.search}>
            <IconSearch/>
          </button>
          <div className="dp-search-wrap" ref={searchBoxRef}>
            <span className="dp-search-ico"><IconSearch/></span>
            <input
              ref={searchRef}
              className="dp-search-input"
              placeholder={t.searchPlaceholder}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <span className="dp-search-kbd">⌘K</span>
            {results.length > 0 && (
              <div className="dp-search-results">
                {results.map(r => (
                  <button key={r.slug} className="dp-search-result" onClick={() => jumpTo(r.category, r.slug)}>
                    <span className="dp-search-result-title">{r.title}</span>
                    <span className="dp-search-result-excerpt">{r.excerpt}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className="dp-btn-back" onClick={() => navigate('/')}>
            <IconArrowLeft/> <span>{t.backHome}</span>
          </button>
          <div className="dp-lang-wrap" ref={langMenuRef}>
            <button className="dp-icon-btn" onClick={() => setLangMenuOpen(v => !v)} aria-label={t.changeLanguage} title={t.changeLanguage}>
              <IconGlobe/><span className="dp-lang-code">{lang.toUpperCase()}</span>
            </button>
            {langMenuOpen && (
              <div className="dp-lang-menu">
                {LANG_OPTIONS.map(opt => (
                  <button
                    key={opt.key}
                    className={`dp-lang-option ${lang === opt.key ? 'dp-lang-option--active' : ''}`}
                    onClick={() => { setLang(opt.key); setLangMenuOpen(false); }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className="dp-icon-btn" onClick={toggleTheme} aria-label={t.toggleTheme} title={t.toggleTheme}>
            {theme === 'dark' ? <IconSun/> : <IconMoon/>}
          </button>
          <button className="dp-btn-login" onClick={() => navigate('/login')}>{t.login}</button>
        </div>
      </header>

      {mobileNavOpen && (
        <div className="dp-mobile-nav dp-mobile-nav--open">
          {Sidebar}
        </div>
      )}

      {mobileSearchOpen && (
        <div className="dp-mobile-search-overlay">
          <div className="dp-mobile-search-bar">
            <span className="dp-search-ico"><IconSearch/></span>
            <input
              ref={mobileSearchRef}
              className="dp-mobile-search-input"
              placeholder={t.searchPlaceholder}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <button className="dp-mobile-search-close" onClick={() => { setMobileSearchOpen(false); setQuery(''); }} aria-label={t.closeSearch}>
              <IconX/>
            </button>
          </div>
          <div className="dp-mobile-search-body">
            {query.trim() === '' ? (
              <div className="dp-mobile-search-hint">{t.mobileSearchHint}</div>
            ) : results.length > 0 ? (
              results.map(r => (
                <button key={r.slug} className="dp-search-result" onClick={() => jumpTo(r.category, r.slug)}>
                  <span className="dp-search-result-title">{r.title}</span>
                  <span className="dp-search-result-excerpt">{r.excerpt}</span>
                </button>
              ))
            ) : (
              <div className="dp-mobile-search-hint">{t.noResultsFor(query)}</div>
            )}
          </div>
        </div>
      )}

      <div className="dp-shell">
        <aside className="dp-sidebar">{Sidebar}</aside>

        <div className="dp-shell-inner">
        {!category ? (
          /* ── DOCS HOME, real content, not a lobby to click through ── */
          <main key="home" className="dp-content dp-home">
            <div className="dp-hero">
              <h1 className="dp-hero-title">{t.heroTitle}</h1>
              <p className="dp-hero-sub">{t.heroSub}</p>
            </div>
            <div className="dp-home-section-label">{t.mostSearched}</div>
            <div className="dp-popular-grid">
              {popularArticles.map(a => (
                <button key={a.slug} className="dp-popular-card" onClick={() => jumpTo(a.category, a.slug)}>
                  <span className="dp-popular-ico"><IconStar/></span>
                  <span>{a.title}</span>
                  <IconChevronRight/>
                </button>
              ))}
            </div>
          </main>
        ) : (
          /* ── CATEGORY PAGE, one continuous scroll, Linear/Vercel-style ── */
          <main key={categoryParam} className="dp-content dp-category">
            <div className="dp-breadcrumb">
              <button onClick={() => navigate('/docs')}>{t.docsLink}</button>
              <IconChevronRight/>
              <span>{category.title}</span>
            </div>

            <h1 className="dp-category-title">{category.title}</h1>
            <p className="dp-category-desc">{category.description}</p>

            {sections.map((a, i) => (
              <section
                key={a.slug}
                id={a.slug}
                ref={el => { sectionRefs.current[a.slug] = el; }}
                data-slug={a.slug}
                className="dp-section"
              >
                {i > 0 && <div className="dp-section-divider"/>}
                <h2 className="dp-section-title">{a.title}</h2>
                <p className="dp-section-excerpt">{a.excerpt}</p>
                <div className="dp-section-body">
                  {a.blocks.map((b, bi) => <Block key={bi} block={b}/>)}
                </div>
              </section>
            ))}

            {faqs.length > 0 && (
              <section
                id={FAQ_ANCHOR}
                ref={el => { sectionRefs.current[FAQ_ANCHOR] = el; }}
                data-slug={FAQ_ANCHOR}
                className="dp-section"
              >
                <div className="dp-section-divider"/>
                <h2 className="dp-section-title">{t.faqTitle}</h2>
                <FaqAccordion faqs={faqs}/>
              </section>
            )}

            <div className="dp-category-nav">
              {prevCategory ? (
                <button className="dp-category-nav-btn dp-category-nav-btn--prev" onClick={() => goToCategory(prevCategory.key)}>
                  <IconArrowLeft/>
                  <span><small>{t.prev}</small>{prevCategory.title}</span>
                </button>
              ) : <span/>}
              {nextCategory && (
                <button className="dp-category-nav-btn dp-category-nav-btn--next" onClick={() => goToCategory(nextCategory.key)}>
                  <span><small>{t.next}</small>{nextCategory.title}</span>
                  <IconArrowRight/>
                </button>
              )}
            </div>
          </main>
        )}

        {category && (
          <aside className="dp-toc">
            <div className="dp-toc-label">{t.onThisPage}</div>
            {sections.map(a => (
              <button
                key={a.slug}
                className={`dp-toc-link ${activeSlug === a.slug ? 'dp-toc-link--active' : ''}`}
                onClick={() => jumpTo(category.key, a.slug)}
              >
                {a.title}
              </button>
            ))}
            {faqs.length > 0 && (
              <button
                className={`dp-toc-link ${activeSlug === FAQ_ANCHOR ? 'dp-toc-link--active' : ''}`}
                onClick={() => jumpTo(category.key, FAQ_ANCHOR)}
              >
                {t.faqTitle}
              </button>
            )}
          </aside>
        )}
        </div>
      </div>

      <Suspense fallback={null}>
        <RuixenGradientFooter className="nx-footer dp-footer" gradientHeight={typeof window !== 'undefined' && window.innerWidth < 640 ? '10vh' : '18vh'}>
          <div className="nx-container">
            <div className="nx-footer-grid">
              <div>
                <div className="nx-footer-socials">
                  <a href="https://instagram.com/contact_cl" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="nx-footer-social">
                    <svg width="15" height="15" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </a>
                  <a href="https://www.linkedin.com/company/contactnetworkcl" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="nx-footer-social">
                    <svg width="15" height="15" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  </a>
                </div>
              </div>
              <div>
                <div className="nx-footer-col-title">{t.footer.product}</div>
                <div className="nx-footer-links">
                  <a href="/#caracteristicas">{t.footer.features}</a>
                  <a href="/#precios">{t.footer.plans}</a>
                  <button onClick={() => navigate('/docs')} className="nx-footer-link-btn">{t.footer.docs}</button>
                  <button onClick={() => navigate('/agenda-demo')} className="nx-footer-link-btn">{t.footer.schedule}</button>
                </div>
              </div>
              <div>
                <div className="nx-footer-col-title">{t.footer.company}</div>
                <div className="nx-footer-links">
                  <a href="/sobre-nosotros">{t.footer.about}</a>
                  <a href="mailto:soporte@contactnetwork.cl">soporte@contactnetwork.cl</a>
                  <a href="tel:+56982036712">+56 9 8203 6712</a>
                </div>
              </div>
              <div>
                <div className="nx-footer-col-title">{t.footer.legal}</div>
                <div className="nx-footer-links">
                  <button onClick={() => navigate('/terminos')} className="nx-footer-link-btn">{t.footer.terms}</button>
                  <button onClick={() => navigate('/privacidad')} className="nx-footer-link-btn">{t.footer.privacy}</button>
                  <button onClick={() => navigate('/tratamiento-de-datos')} className="nx-footer-link-btn">{t.footer.dataProcessing}</button>
                  <span className="nx-footer-detail">{t.footer.hours}</span>
                </div>
              </div>
              <div>
                <button className="nx-footer-sec-btn" onClick={() => setShowSecurity(s => !s)}>
                  <span className="nx-footer-col-title" style={{margin:0}}>{t.footer.security}</span>
                  <svg className={'nx-footer-sec-chevron' + (showSecurity ? ' nx-footer-sec-open' : '')} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
                </button>
                {showSecurity && (
                  <p className="nx-footer-sec-text">{t.footer.securityText}</p>
                )}
              </div>
            </div>
            <div className="nx-footer-bottom">
              <span style={{fontFamily:"'Inter',system-ui,sans-serif",fontWeight:300,letterSpacing:'-.025em'}}>{t.footer.copyright}</span>
              <span>{t.footer.tagline}</span>
            </div>
          </div>
        </RuixenGradientFooter>
      </Suspense>

      {[
        { show: showTermsModal, close: () => setShowTermsModal(false), data: LEGAL_ES.terms },
        { show: showPrivacyModal, close: () => setShowPrivacyModal(false), data: LEGAL_ES.privacy },
        { show: showDataProcessingModal, close: () => setShowDataProcessingModal(false), data: LEGAL_ES.dataProcessing },
      ].map(({ show, close, data }) => show && (
        <div key={data.title} className="dp-modal-overlay" onClick={e => e.target === e.currentTarget && close()}>
          <div className="dp-modal">
            <div className="dp-modal-head">
              <h2 className="dp-modal-h2">{data.title}</h2>
              <button onClick={close} className="dp-modal-close" aria-label={t.closeModal}>
                <IconX/>
              </button>
            </div>
            <div className="dp-modal-body">
              <strong className="dp-modal-heading">{data.heading}</strong>
              {data.sections.map(([title, body], i) => (
                <section key={i} className="dp-modal-section">
                  <h4>{title}</h4>
                  {body.split('\n\n').map((p, j) => <p key={j}>{p}</p>)}
                </section>
              ))}
            </div>
          </div>
        </div>
      ))}

      <style>{CSS}</style>
    </div>
  );
};

/* ─── CSS ──────────────────────────────── */
const CSS = `
:root{
  --dp-bg:#FAFAF9;
  --dp-surface:#FFFFFF;
  --dp-text:#111111;
  --dp-muted:#6E6E73;
  --dp-faint:#AEAEB2;
  --dp-border:rgba(0,0,0,.08);
  --dp-border-dk:rgba(0,0,0,.14);
  --dp-blue:#0071E3;
  --dp-overlay:rgba(0,0,0,.4);
  --dp-shadow:rgba(0,0,0,.14);
  --dp-footer-icon-border:rgba(0,0,0,.08);
  --dp-footer-icon-color:rgba(0,0,0,.5);
  --dp-footer-chevron:rgba(0,0,0,.4);
  --dp-topbar-bg:rgba(250,250,249,.88);
}

.dp-root[data-theme="dark"]{
  --dp-bg:#0B0B0D;
  --dp-surface:#15161A;
  --dp-text:#F2F2F3;
  --dp-muted:#9A9AA2;
  --dp-faint:#6B6B73;
  --dp-border:rgba(255,255,255,.09);
  --dp-border-dk:rgba(255,255,255,.16);
  --dp-blue:#4DA3FF;
  --dp-overlay:rgba(0,0,0,.6);
  --dp-shadow:rgba(0,0,0,.5);
  --dp-footer-icon-border:rgba(255,255,255,.12);
  --dp-footer-icon-color:rgba(255,255,255,.6);
  --dp-footer-chevron:rgba(255,255,255,.45);
  --dp-topbar-bg:rgba(11,11,13,.82);
}

.dp-root{font-family:'Inter',system-ui,sans-serif;background:var(--dp-bg);color:var(--dp-text);min-height:100vh;line-height:1.6;transition:background .2s ease,color .2s ease}

@keyframes dpFadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}

/* ── TOPBAR ── */
.dp-topbar{position:sticky;top:0;z-index:50;background:var(--dp-topbar-bg);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);border-bottom:1px solid var(--dp-border)}
.dp-topbar-inner{padding:0 24px;height:56px;display:flex;align-items:center;gap:16px}
.dp-topbar-left{display:flex;align-items:center;gap:14px;flex-shrink:0}
.dp-mobile-menu-btn{display:none;background:none;border:none;color:var(--dp-text);cursor:pointer;padding:4px}
.dp-mobile-search-btn{display:none;flex-shrink:0;background:none;border:none;color:var(--dp-text);cursor:pointer;padding:4px;margin-left:auto}
.dp-topbar-divider{width:1px;height:18px;background:var(--dp-border-dk)}
.dp-topbar-docslink{background:none;border:none;font-family:inherit;font-size:14px;font-weight:600;color:var(--dp-text);cursor:pointer;padding:0}
.dp-search-wrap{position:relative;flex:1;max-width:440px}
.dp-search-ico{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:var(--dp-faint);pointer-events:none;transition:color .15s}
.dp-search-input{width:100%;height:36px;border-radius:8px;border:1px solid var(--dp-border-dk);background:var(--dp-surface);padding:0 44px;font-family:inherit;font-size:13.5px;color:var(--dp-text);outline:none;transition:border-color .18s,box-shadow .18s}
.dp-search-input:focus{border-color:var(--dp-blue);box-shadow:0 0 0 3px rgba(0,113,227,.12)}
.dp-search-input:focus + .dp-search-kbd{opacity:0}
.dp-search-kbd{position:absolute;right:10px;top:50%;transform:translateY(-50%);font-size:11px;color:var(--dp-faint);border:1px solid var(--dp-border-dk);border-radius:4px;padding:1px 6px;pointer-events:none;transition:opacity .15s}
.dp-search-results{position:absolute;top:calc(100% + 8px);left:0;right:0;background:var(--dp-surface);border:1px solid var(--dp-border-dk);border-radius:12px;box-shadow:0 16px 40px var(--dp-shadow);overflow:hidden;max-height:420px;overflow-y:auto;z-index:60;animation:dpFadeIn .15s ease both}
.dp-search-result{display:flex;flex-direction:column;gap:2px;width:100%;text-align:left;background:none;border:none;border-bottom:1px solid var(--dp-border);padding:10px 14px;cursor:pointer;font-family:inherit;transition:background .12s}
.dp-search-result:last-child{border-bottom:none}
.dp-search-result:hover{background:var(--dp-bg)}
.dp-search-result-title{font-size:13.5px;font-weight:600;color:var(--dp-text)}
.dp-search-result-excerpt{font-size:12px;color:var(--dp-muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dp-btn-back{display:inline-flex;align-items:center;gap:6px;background:none;border:none;color:var(--dp-muted);font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;padding:0;white-space:nowrap;flex-shrink:0;transition:color .15s}
.dp-btn-back:hover{color:var(--dp-text)}
.dp-icon-btn{flex-shrink:0;display:flex;align-items:center;gap:4px;background:none;border:1px solid var(--dp-border-dk);color:var(--dp-muted);border-radius:8px;height:32px;padding:0 9px;cursor:pointer;transition:color .15s,border-color .15s,background .15s;font-family:inherit;font-size:12px;font-weight:600}
.dp-icon-btn:hover{color:var(--dp-text);border-color:var(--dp-faint);background:var(--dp-surface)}
.dp-lang-wrap{position:relative;flex-shrink:0}
.dp-lang-code{letter-spacing:.02em}
.dp-lang-menu{position:absolute;top:calc(100% + 8px);right:0;background:var(--dp-surface);border:1px solid var(--dp-border-dk);border-radius:10px;box-shadow:0 12px 30px var(--dp-shadow);overflow:hidden;z-index:60;min-width:72px;animation:dpFadeIn .15s ease both}
.dp-lang-option{display:block;width:100%;text-align:left;background:none;border:none;font-family:inherit;font-size:13px;font-weight:500;color:var(--dp-text);cursor:pointer;padding:8px 14px;transition:background .12s}
.dp-lang-option:hover{background:var(--dp-bg)}
.dp-lang-option--active{color:var(--dp-blue);font-weight:700}
.dp-btn-login{flex-shrink:0;background:var(--dp-text);color:var(--dp-bg);border:none;border-radius:100px;height:32px;padding:0 18px;font-family:inherit;font-size:13px;font-weight:600;cursor:pointer;white-space:nowrap;transition:opacity .15s,transform .15s}
.dp-btn-login:hover{opacity:.85;transform:translateY(-1px)}

.dp-mobile-nav{display:none}

/* ── MOBILE SEARCH OVERLAY ── */
.dp-mobile-search-overlay{display:none}
@media(max-width:860px){
  .dp-mobile-search-overlay{display:flex;flex-direction:column;position:fixed;inset:0;background:var(--dp-bg);z-index:70}
}
.dp-mobile-search-bar{display:flex;align-items:center;gap:10px;flex-shrink:0;padding:12px 16px;padding-top:calc(12px + env(safe-area-inset-top, 0px));border-bottom:1px solid var(--dp-border)}
.dp-mobile-search-bar .dp-search-ico{position:static;color:var(--dp-faint)}
.dp-mobile-search-input{flex:1;height:40px;border:none;background:none;font-family:inherit;font-size:16px;color:var(--dp-text);outline:none}
.dp-mobile-search-close{flex-shrink:0;background:none;border:none;color:var(--dp-muted);cursor:pointer;padding:6px}
.dp-mobile-search-body{flex:1;overflow-y:auto;padding:8px 10px}
.dp-mobile-search-hint{padding:32px 16px;text-align:center;font-size:13.5px;color:var(--dp-faint)}
.dp-mobile-search-body .dp-search-result{border-bottom:1px solid var(--dp-border);border-radius:8px;padding:14px 10px}
.dp-mobile-search-body .dp-search-result-excerpt{white-space:normal}

/* ── SHELL (sidebar always visible, Vercel/Linear-style) ──
   The sidebar is pinned to the real left edge of the window (position:fixed),
   not centered inside a max-width wrapper, so it never leaves a dead gap on
   wide monitors the way a "whole 3-column block centered" layout would.
   Everything else lives in .dp-shell-inner, which starts right after the
   sidebar's width and centers its own, narrower max-width within whatever
   space remains, however wide the screen is. */
.dp-shell{margin-left:248px}
.dp-shell-inner{display:grid;grid-template-columns:minmax(0,1fr) 220px;gap:40px;max-width:1040px;margin:0 auto;padding:0 24px;align-items:start}

.dp-sidebar{position:fixed;top:56px;left:0;bottom:0;width:248px;display:flex;flex-direction:column;padding:28px 4px 20px 32px;border-right:1px solid var(--dp-border);background:var(--dp-bg)}
.dp-sidebar-nav{flex:1;overflow-y:auto;display:flex;flex-direction:column;gap:18px;padding-right:12px;background:transparent;backdrop-filter:none;-webkit-backdrop-filter:none;border-bottom:none}
.dp-sidebar-group{display:flex;flex-direction:column;gap:1px}
.dp-sidebar-cat{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:700;color:var(--dp-text);background:none;border:none;font-family:inherit;cursor:pointer;padding:5px 8px;margin:0 0 2px -8px;text-align:left;border-radius:7px;transition:background .15s,color .15s}
.dp-sidebar-cat:hover{background:var(--dp-border)}
.dp-sidebar-cat--active{color:var(--dp-blue)}
.dp-sidebar-cat-ico{display:flex;color:inherit;flex-shrink:0}
.dp-sidebar-arts{display:flex;flex-direction:column;gap:1px;padding-left:16px;border-left:1px solid var(--dp-border);margin:2px 0 10px 11px}
.dp-sidebar-art{display:flex;align-items:center;text-align:left;background:none;border:none;font-family:inherit;font-size:13px;color:var(--dp-muted);cursor:pointer;padding:5px 10px 5px 14px;margin-left:-1px;border-radius:0 7px 7px 0;transition:background .15s,color .15s}
.dp-sidebar-art:hover{color:var(--dp-text);background:var(--dp-border)}
.dp-sidebar-art--active{color:var(--dp-blue);font-weight:600;background:rgba(0,113,227,.1);border-left:2px solid var(--dp-blue);padding-left:13px;margin-left:-1px}
.dp-sidebar-footer{flex-shrink:0;padding-top:14px;border-top:1px solid var(--dp-border)}
.dp-sidebar-footer-link{display:flex;align-items:center;gap:8px;background:none;border:none;font-family:inherit;font-size:13px;font-weight:500;color:var(--dp-muted);cursor:pointer;padding:6px 8px;border-radius:7px;transition:background .15s,color .15s}
.dp-sidebar-footer-link:hover{color:var(--dp-text);background:var(--dp-border)}

.dp-content{min-width:0;padding:40px 0 120px;animation:dpFadeIn .3s cubic-bezier(.16,1,.3,1) both}

/* ── HOME ── */
.dp-home{max-width:640px}
.dp-hero{margin-bottom:40px}
.dp-hero-title{font-size:clamp(28px,3.6vw,38px);font-weight:300;letter-spacing:-.02em;margin-bottom:12px}
.dp-hero-sub{font-size:15.5px;color:var(--dp-muted);line-height:1.6}
.dp-home-section-label{font-size:11px;font-weight:700;color:var(--dp-faint);text-transform:uppercase;letter-spacing:.08em;margin-bottom:14px}
.dp-popular-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:10px}
.dp-popular-card{display:flex;align-items:center;gap:10px;background:var(--dp-surface);border:1px solid var(--dp-border);border-radius:10px;padding:14px 16px;font-family:inherit;font-size:13.5px;font-weight:500;color:var(--dp-text);cursor:pointer;text-align:left;transition:border-color .18s,transform .18s,box-shadow .18s}
.dp-popular-card:hover{border-color:var(--dp-border-dk);transform:translateY(-2px);box-shadow:0 8px 20px var(--dp-shadow)}
.dp-popular-ico{color:var(--dp-blue);flex-shrink:0}
.dp-popular-card svg:last-child{margin-left:auto;color:var(--dp-faint);flex-shrink:0;transition:transform .18s}
.dp-popular-card:hover svg:last-child{transform:translateX(2px)}

/* ── CATEGORY CONTENT ── */
.dp-category{max-width:700px}
.dp-breadcrumb{display:flex;align-items:center;gap:6px;font-size:13px;color:var(--dp-faint);margin-bottom:18px}
.dp-breadcrumb button{background:none;border:none;font-family:inherit;font-size:13px;color:var(--dp-faint);cursor:pointer;padding:0;transition:color .15s}
.dp-breadcrumb button:hover{color:var(--dp-muted)}
.dp-category-title{font-size:clamp(28px,3.4vw,36px);font-weight:300;letter-spacing:-.02em;margin-bottom:8px}
.dp-category-desc{font-size:15.5px;color:var(--dp-muted);margin-bottom:8px}

.dp-section{scroll-margin-top:72px}
.dp-section-divider{border-top:1px solid var(--dp-border);margin:38px 0}
.dp-section-title{font-size:21px;font-weight:600;letter-spacing:-.01em;margin-bottom:8px}
.dp-section-excerpt{font-size:14.5px;color:var(--dp-muted);margin-bottom:20px}
.dp-section-body{font-size:15px;color:var(--dp-text)}
.dp-p{margin-bottom:17px;line-height:1.75}
.dp-h4{font-size:15.5px;font-weight:600;margin:24px 0 11px;letter-spacing:-.005em}
.dp-list,.dp-steps{margin:0 0 18px;padding-left:22px;display:flex;flex-direction:column;gap:7px}
.dp-list li,.dp-steps li{line-height:1.6}
.dp-table-wrap{overflow-x:auto;margin-bottom:22px;border:1px solid var(--dp-border);border-radius:10px}
.dp-table{width:100%;border-collapse:collapse;font-size:13.5px}
.dp-table th{text-align:left;background:var(--dp-bg);font-weight:600;padding:9px 13px;border-bottom:1px solid var(--dp-border);white-space:nowrap}
.dp-table td{padding:9px 13px;border-bottom:1px solid var(--dp-border);vertical-align:top}
.dp-table tr:last-child td{border-bottom:none}
.dp-callout{display:flex;gap:10px;align-items:flex-start;border-radius:10px;padding:13px 15px;font-size:13.5px;line-height:1.6;margin-bottom:22px}
.dp-callout-ico{flex-shrink:0;margin-top:2px}
.dp-callout--info,.dp-callout--pro{background:rgba(0,113,227,.09);color:var(--dp-text)}
.dp-callout--info .dp-callout-ico,.dp-callout--pro .dp-callout-ico{color:var(--dp-blue)}
.dp-callout--warn{background:rgba(220,38,38,.09);color:var(--dp-text)}
.dp-callout--warn .dp-callout-ico{color:#DC2626}

.dp-faq-list{display:flex;flex-direction:column;gap:2px}
.dp-faq-item{border-bottom:1px solid var(--dp-border)}
.dp-faq-q{display:flex;align-items:center;justify-content:space-between;gap:12px;width:100%;text-align:left;background:none;border:none;font-family:inherit;font-size:14.5px;font-weight:600;color:var(--dp-text);cursor:pointer;padding:15px 2px;transition:color .15s}
.dp-faq-q:hover{color:var(--dp-blue)}
.dp-faq-chevron{flex-shrink:0;color:var(--dp-faint);transition:transform .2s}
.dp-faq-item--open .dp-faq-chevron{transform:rotate(180deg)}
.dp-faq-a{font-size:14px;color:var(--dp-muted);line-height:1.65;padding:0 2px 16px;animation:dpFadeIn .2s ease both}

.dp-category-nav{display:flex;justify-content:space-between;gap:16px;margin-top:52px;padding-top:26px;border-top:1px solid var(--dp-border)}
.dp-category-nav-btn{display:flex;align-items:center;gap:10px;background:var(--dp-surface);border:1px solid var(--dp-border);border-radius:10px;padding:12px 16px;font-family:inherit;font-size:13.5px;font-weight:600;color:var(--dp-text);cursor:pointer;max-width:48%;transition:border-color .18s,transform .18s}
.dp-category-nav-btn:hover{border-color:var(--dp-border-dk);transform:translateY(-1px)}
.dp-category-nav-btn span{display:flex;flex-direction:column;gap:2px;text-align:left}
.dp-category-nav-btn--next{margin-left:auto;text-align:right}
.dp-category-nav-btn--next span{text-align:right}
.dp-category-nav-btn small{font-size:10.5px;font-weight:600;color:var(--dp-faint);text-transform:uppercase;letter-spacing:.04em}

.dp-toc{position:sticky;top:56px;height:calc(100vh - 56px);overflow-y:auto;display:flex;flex-direction:column;gap:1px;padding:28px 0 20px 18px;border-left:1px solid var(--dp-border)}
.dp-toc-label{font-size:11px;font-weight:700;color:var(--dp-faint);text-transform:uppercase;letter-spacing:.06em;margin-bottom:10px}
.dp-toc-link{text-align:left;background:none;border:none;border-left:2px solid transparent;margin-left:-19px;padding:5px 0 5px 17px;font-family:inherit;font-size:12.5px;color:var(--dp-muted);cursor:pointer;line-height:1.6;transition:color .15s,border-color .15s}
.dp-toc-link:hover{color:var(--dp-text)}
.dp-toc-link--active{color:var(--dp-blue);border-color:var(--dp-blue);font-weight:600}

/* ── MOBILE ── */
@media(max-width:1180px){
  .dp-sidebar{width:224px}
  .dp-shell{margin-left:224px}
  .dp-shell-inner{grid-template-columns:minmax(0,1fr)}
  .dp-toc{display:none}
}
@media(max-width:860px){
  .dp-mobile-menu-btn{display:flex}
  .dp-mobile-search-btn{display:flex}
  .dp-topbar-docslink,.dp-topbar-divider{display:none}
  .dp-search-wrap,.dp-btn-back{display:none}
  .dp-popular-grid{grid-template-columns:1fr;margin-bottom:24px}
  .dp-shell{margin-left:0}
  .dp-shell-inner{grid-template-columns:minmax(0,1fr);padding:0 20px;max-width:none}
  .dp-sidebar{display:none}
  .dp-content{padding:28px 0 40px}
  .dp-mobile-nav--open{display:flex;flex-direction:column;position:fixed;top:56px;left:0;right:0;bottom:0;background:var(--dp-bg);z-index:40;padding:20px 20px 0}
  .dp-mobile-nav--open .dp-sidebar-nav{padding-right:0}
  .dp-mobile-nav--open .dp-sidebar-footer{padding-bottom:16px}
  .dp-category-title{font-size:26px}
  .dp-section-title{font-size:19px}
  .dp-lang-code{display:none}
  .dp-icon-btn{padding:0 7px}
}

/* ── FOOTER (shared Homepage footer, reused here) ── */
.nx-container{max-width:1280px;margin:0 auto;padding:0 40px}
.dp-footer{background:var(--dp-bg);padding:48px 0 16px;margin-top:40px}
.nx-footer-grid{display:grid;grid-template-columns:2fr 1fr 1fr 1fr 1fr;gap:14px;padding-bottom:14px;border-bottom:1px solid var(--dp-border)}
.nx-footer-sec-btn{display:flex;align-items:center;gap:6px;background:none;border:none;cursor:pointer;padding:0;margin-bottom:6px;font-family:inherit}
.nx-footer-sec-chevron{color:var(--dp-footer-chevron);transition:transform .2s ease;flex-shrink:0;margin-top:1px}
.nx-footer-sec-open{transform:rotate(180deg)}
.nx-footer-sec-text{font-size:11px;color:var(--dp-muted);line-height:1.4;margin:0}
.nx-footer-socials{display:flex;gap:8px;margin-top:6px}
.nx-footer-social{width:22px;height:22px;border-radius:6px;background:var(--dp-border);border:1px solid var(--dp-footer-icon-border);color:var(--dp-footer-icon-color);display:flex;align-items:center;justify-content:center;text-decoration:none;transition:all .15s}
.nx-footer-social:hover{border-color:var(--dp-text);color:var(--dp-text)}
.nx-footer-col-title{font-size:10px;font-weight:400;color:var(--dp-text);text-transform:uppercase;letter-spacing:.07em;margin-bottom:6px}
.nx-footer-links{display:flex;flex-direction:column;gap:5px}
.nx-footer-links a{font-size:12px;color:var(--dp-muted);text-decoration:none;transition:color .15s}
.nx-footer-links a:hover{color:var(--dp-text)}
.nx-footer-link-btn{background:none;border:none;cursor:pointer;font-family:inherit;font-size:12px;color:var(--dp-muted);text-align:left;padding:0;transition:color .15s}
.nx-footer-link-btn:hover{color:var(--dp-text)}
.nx-footer-detail{font-size:11px;color:var(--dp-faint)}
.nx-footer-bottom{padding:6px 0;display:flex;align-items:center;justify-content:space-between;gap:14px}
.nx-footer-bottom>span{font-size:11px;color:var(--dp-faint)}
@media(max-width:1024px){
  .nx-container{padding:0 28px}
  .nx-footer-grid{grid-template-columns:1fr 1fr;gap:32px}
}
@media(max-width:640px){
  .nx-container{padding:0 18px}
  .dp-footer{padding:20px 0 10px;margin-top:0}
  .nx-footer-grid{grid-template-columns:1fr;gap:18px;padding-bottom:16px}
  .nx-footer-bottom{flex-wrap:wrap;justify-content:center;gap:6px 16px;text-align:center;padding:6px 0}
  .nx-footer-bottom>span{font-size:11px}
}

/* ── LEGAL MODALS ── */
.dp-modal-overlay{position:fixed;inset:0;background:var(--dp-overlay);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;z-index:200;padding:20px}
.dp-modal{background:var(--dp-surface);border-radius:14px;max-width:640px;width:100%;max-height:80vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 24px 64px var(--dp-shadow)}
.dp-modal-head{display:flex;align-items:center;justify-content:space-between;padding:20px 24px;border-bottom:1px solid var(--dp-border);flex-shrink:0}
.dp-modal-h2{font-size:17px;font-weight:600;margin:0}
.dp-modal-close{background:none;border:none;color:var(--dp-muted);cursor:pointer;padding:4px;transition:color .15s}
.dp-modal-close:hover{color:var(--dp-text)}
.dp-modal-body{padding:24px;overflow-y:auto}
.dp-modal-heading{display:block;text-align:center;font-size:14px;color:var(--dp-text);margin-bottom:20px}
.dp-modal-section{margin-bottom:18px}
.dp-modal-section h4{font-size:13px;font-weight:700;color:var(--dp-text);margin-bottom:6px}
.dp-modal-section p{font-size:13px;color:var(--dp-muted);line-height:1.7;margin-top:6px}

@media(prefers-reduced-motion:reduce){
  .dp-root *{animation-duration:.01ms !important;transition-duration:.01ms !important;scroll-behavior:auto !important}
}
`;

export default DocsPage;
